// src/pages/PrintVinosPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getToken } from "../services/authService";
import "./print-menu.css"; // reutiliza el CSS del menú (ya trae estilos bilingües)
import logo from "../assets/logo-melonares.png";


function pickBoth(obj, base) {
    const es = (obj?.[`${base}_es`] ?? obj?.[base] ?? "").toString().trim();
    const en = (obj?.[`${base}_en`] ?? obj?.[base] ?? "").toString().trim();
    return { es, en };
}

const STAGES = [
    { key: "ap", es: "Aperitivo", en: "Aperitif" },
    { key: "pr", es: "Primer plato", en: "First course" },
    { key: "po", es: "Postre", en: "Dessert" },
];

export default function PrintVinosPage() {
    const [params] = useSearchParams();
    const apParam = params.get("apIds") || "";
    const prParam = params.get("prIds") || "";
    const poParam = params.get("poIds") || "";
    const auto = params.get("auto") === "1";

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [wines, setWines] = useState([]);
    const [assetsReady, setAssetsReady] = useState(false);
    const [logoFailed, setLogoFailed] = useState(false);

    // Carga todos los vinos y luego filtramos por IDs de etapas
    useEffect(() => {
        let cancelled = false;
        async function load() {
            setLoading(true);
            setError("");
            try {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/api/vinos/all`, {
                    headers: { Authorization: `Bearer ${getToken()}` },
                });
                if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
                const data = await res.json();
                if (!cancelled) setWines(Array.isArray(data) ? data : []);
            } catch (e) {
                if (!cancelled) setError("Error cargando vinos");
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        load();
        return () => { cancelled = true; };
    }, []);

    // Construye arrays por etapa respetando el orden del querystring y máx 2
    const stageIds = useMemo(() => {
        const parseIds = (s) =>
            s.split(",").map(t => t.trim()).filter(Boolean);
        return {
            ap: parseIds(apParam).slice(0, 2),
            pr: parseIds(prParam).slice(0, 2),
            po: parseIds(poParam).slice(0, 2),
        };
    }, [apParam, prParam, poParam]);

    const selectedByStage = useMemo(() => {
        if (!wines.length) return { ap: [], pr: [], po: [] };
        const map = new Map(wines.map(w => [String(w.id), w]));
        const enrich = (id) => {
            const v = map.get(String(id));
            if (!v) return null;
            const { es: name_es, en: name_en } = pickBoth(v, "nombre");
            const { es: desc_es, en: desc_en } = pickBoth(v, "descripcion");
            const precio = isFinite(Number(v.precio)) ? Number(v.precio) : null;
            return {
                ...v,
                _nombre_es: (name_es || v.nombre || "—").trim(),
                _nombre_en: (name_en || name_es || v.nombre || "—").trim(),
                _desc_es: (desc_es || "").trim(),
                _desc_en: (desc_en || "").trim(),
                _precio: precio,
            };
        };
        return {
            ap: stageIds.ap.map(enrich).filter(Boolean),
            pr: stageIds.pr.map(enrich).filter(Boolean),
            po: stageIds.po.map(enrich).filter(Boolean),
        };
    }, [wines, stageIds]);

    // Espera a que el logo cargue (si existe)
    useEffect(() => {
        if (loading || error) return;
        const sheet = document.querySelector(".sheet");
        if (!sheet) return;
        const imgs = Array.from(sheet.querySelectorAll("img"));
        if (imgs.length === 0) { setAssetsReady(true); return; }
        let pending = imgs.length;
        const done = () => { pending -= 1; if (pending <= 0) setAssetsReady(true); };
        imgs.forEach((img) => {
            if (img.complete && img.naturalWidth > 0) done();
            else {
                img.addEventListener("load", done, { once: true });
                img.addEventListener("error", done, { once: true });
            }
        });
        return () => {
            imgs.forEach((img) => { img.onload = null; img.onerror = null; });
        };
    }, [loading, error, selectedByStage.ap.length, selectedByStage.pr.length, selectedByStage.po.length]);

    // Imprimir al estar listo
    useEffect(() => {
        if (!loading && !error && assetsReady) {
            const t = setTimeout(() => window.print(), auto ? 120 : 0);
            const after = () => window.close?.();
            window.addEventListener("afterprint", after, { once: true });
            return () => {
                clearTimeout(t);
                window.removeEventListener("afterprint", after);
            };
        }
    }, [loading, error, assetsReady, auto]);

    const hasAny = selectedByStage.ap.length || selectedByStage.pr.length || selectedByStage.po.length;

    if (loading) {
        return (
            <div className="print-wrap luxe">
                <div className="no-print preloader">Cargando…</div>
            </div>
        );
    }
    if (error || !hasAny) {
        return (
            <div className="print-wrap luxe">
                <div className="no-print preloader danger">
                    {error || "No hay vinos seleccionados"}
                </div>
            </div>
        );
    }

    return (
        <div className="print-wrap luxe">
            <div className="sheet frame">
                {/* Cabecera con marca */}
                <header className="brand">
                    {logoFailed ? (
                        <>
                            <div className="monogram">M</div>
                            <div className="brand-name">LOS MELONARES</div>
                        </>
                    ) : (
                        <img
                            className="brand-logo"
                            alt="Los Melonares"
                            src={logo}
                            onError={() => setLogoFailed(true)}
                            style={{ width: "50mm", height: "auto", display: "block" }}
                        />
                    )}
                </header>

                {/* Cuerpo bilingüe */}
                <main className="sheet-body">
                    <div className="menu-main">
                        {/* Título principal ES / EN */}
                        <h1 className="menu-title bilingual-slash">
                            <span className="es">Selección de vinos</span>
                            <span className="slash"> / </span>
                            <span className="en">Wine selection</span>
                        </h1>

                        {/* Etapas: Aperitivo / 1º plato / Postre */}
                        {STAGES.map((st) => {
                            const arr = selectedByStage[st.key];
                            if (!arr.length) return null;
                            return (
                                <section key={st.key} className="course">
                                    <h2 className="course-title bilingual-slash">
                                        <span className="es">{st.es}</span>
                                        <span className="slash"> / </span>
                                        <span className="en">{st.en}</span>
                                    </h2>

                                    {/* === NUEVO: dos columnas (ES izquierda / EN derecha) === */}
                                    <div className="bilingual-columns">
                                        {/* Columna ES */}
                                        <div className="lang-col es">
                                            <div className="lang-heading">Español</div>
                                            {arr.map((v, i) => (
                                                <div key={`es-${st.key}-${v.id ?? i}`} className="dish-block">
                                                    <div className="dish-name-es">
                                                        {v._nombre_es}
                                                    </div>
                                                    {(() => {
                                                        const txt = v._desc_es;
                                                        return txt ? <div className="dish-desc-es">{txt}</div> : null;
                                                    })()}
                                                    {i < arr.length - 1 && <div className="mini-divider" />}
                                                </div>
                                            ))}
                                        </div>

                                        {/* Columna EN */}
                                        <div className="lang-col en">
                                            <div className="lang-heading">English</div>
                                            {arr.map((v, i) => (
                                                <div key={`en-${st.key}-${v.id ?? i}`} className="dish-block">
                                                    <div className="dish-name-en">
                                                        {v._nombre_en}
                                                    </div>
                                                    {(() => {
                                                        const txt = v._desc_en;
                                                        return txt ? <div className="dish-desc-en">{txt}</div> : null;
                                                    })()}
                                                    {i < arr.length - 1 && <div className="mini-divider" />}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="course-divider" />
                                </section>

                            );
                        })}
                    </div>
                </main>
            </div>
        </div>
    );
}